// Der Slot-Mechanismus fuer die EINE App-Kopfzeile.
//
// WOFUER: bis hierher hat jede Route ihren eigenen Seitenkopf gebaut — im Audit
// 8 verschiedene Bauarten ueber 15 Routen mit 4 H1-Varianten. Ab jetzt gibt es
// genau EINE Kopfzeile (components/AppHeader.tsx); Routen BEFUELLEN sie nur noch
// ueber `usePageHeader(...)`, statt eine eigene zu rendern.
//
// Umgesetzt als Modul-Signal — die dokumentierte Konvention dieses Projekts
// (vgl. `openProjectTaskSig` in store/deepLinks.ts). Kein Context, kein
// Prop-Drilling durch Layout hindurch.

import { signal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { Crumb } from "../components/ui/PageHeader";

export type { Crumb };

export interface PageHeaderConfig {
  /**
   * Bewusst `string` (und nicht wie bei `PageHeader` `ComponentChildren`):
   * der Titel muss vergleichbar sein, damit der Hook unten eine flache
   * Abhaengigkeit daraus bilden kann. Ein Knoten waere bei jedem Render neu.
   */
  title: string;
  /**
   * Ersetzt die DARSTELLUNG des Titels (z. B. ein inline editierbares Feld auf
   * der Projektseite). `title` bleibt PFLICHT und dient weiter als
   * Vergleichsschluessel und als Klartext fuer Dokumenttitel/Vorlesbarkeit.
   *
   * Knoten — daher NICHT Teil der Signatur, sondern wie `tabs`/`actions`
   * identitaetsgeprueft nachgezogen.
   */
  titleNode?: ComponentChildren;
  subtitle?: string;
  breadcrumb?: Crumb[];
  /** Knoten — typischerweise `<ViewTabs …/>`. */
  tabs?: ComponentChildren;
  /** Knoten — typischerweise eine Button-Gruppe. */
  actions?: ComponentChildren;
}

/**
 * Was die aktuelle Route in die Kopfzeile schreibt. `null` = kein Titelbereich.
 *
 * EINZIGER Leser ist `components/AppHeader.tsx`. Eine Route, die dieses Signal
 * setzt, darf es NICHT im selben Bauteil auch lesen — Lesen und Schreiben in
 * einer Komponente ist der klassische Weg in eine Render-Schleife.
 */
export const pageHeaderSig = signal<PageHeaderConfig | null>(null);

/**
 * Ob die Seitenleiste auf schmalen Fenstern als Overlay offen ist.
 *
 * Liegt hier und nicht in `Sidebar.tsx`, weil BEIDE Seiten es brauchen: die
 * Kopfzeile setzt es (Menue-Ausloeser links), die Seitenleiste liest es. Ein
 * neutraler Ort verhindert den Import-Zyklus AppHeader <-> Sidebar.
 */
export const sidebarOpenSig = signal(false);

/**
 * Flache Signatur aus den PRIMITIVWERTEN der Konfiguration.
 *
 * DIE FALLE: `useEffect(fn, [config])` mit einem Objekt-Literal als Abhaengigkeit
 * vergleicht Objekt-IDENTITAETEN. Ein Literal ist bei jedem Render neu, der
 * Effect feuert also bei JEDEM Render — inklusive Aufraeum-Lauf. Die Kopfzeile
 * wuerde pro Render einmal auf `null` fallen und wieder gesetzt (Flackern), und
 * sobald irgendwo ein Render an dem Signal haengt, dreht sich das im Kreis.
 *
 * LOESUNG: Der Auf-/Abbau-Effect haengt an dieser Zeichenkette, nicht am Objekt.
 * `titleNode`, `tabs` und `actions` sind Knoten und daher NICHT Teil der
 * Signatur — sie werden weiter unten identitaetsgeprueft nachgezogen, ohne den
 * Effect neu aufzubauen. Der Klartext-`title` ist und bleibt der Schluessel.
 */
function signature(config: PageHeaderConfig): string {
  const crumbs = (config.breadcrumb ?? [])
    .map((c) => `${c.label} ${c.href ?? ""}`)
    .join("");
  return `${config.title}${config.subtitle ?? ""}${crumbs}`;
}

/**
 * Wer den Kopf gerade besitzt. Beim Routenwechsel kann die NEUE Route schon
 * gesetzt haben, bevor das Cleanup der alten laeuft — ein blindes `= null`
 * wuerde ihren Kopf wieder loeschen. Ein Besitz-Merkmal statt eines
 * Wertvergleichs, weil der Signalwert unten noch nachgezogen wird.
 */
let headerOwner: object | null = null;

/**
 * Fuellt die App-Kopfzeile fuer die Lebensdauer der aufrufenden Route und
 * raeumt beim Unmount auf, damit keine Route den Kopf der naechsten stehen laesst.
 */
export function usePageHeader(config: PageHeaderConfig): void {
  const sig = signature(config);

  // Immer die frischeste Konfiguration greifbar halten, ohne sie zur
  // Abhaengigkeit zu machen (Knoten schliessen ueber aktuellen State ab).
  const latest = useRef(config);
  latest.current = config;

  // Stabiles Besitz-Merkmal dieser Hook-Instanz.
  const token = useRef<object>({});

  useEffect(() => {
    const mine = token.current;
    headerOwner = mine;
    pageHeaderSig.value = { ...latest.current };
    return () => {
      if (headerOwner !== mine) return;
      headerOwner = null;
      pageHeaderSig.value = null;
    };
  }, [sig]);

  // Knoten (`tabs`/`actions`) nachziehen, ohne den Effect oben abzureissen.
  // Bewusst OHNE Abhaengigkeitsliste: laeuft nach jedem Render, schreibt aber
  // nur, wenn sich eine Knoten-Identitaet wirklich geaendert hat. Ein Schreiben
  // rendert nur die Kopfzeile neu (sie liest das Signal), nicht die Route —
  // deshalb kann daraus keine Schleife entstehen.
  useEffect(() => {
    if (headerOwner !== token.current) return;
    const current = pageHeaderSig.value;
    if (!current) return;
    if (
      current.titleNode === config.titleNode &&
      current.tabs === config.tabs &&
      current.actions === config.actions
    ) {
      return;
    }
    pageHeaderSig.value = {
      ...current,
      titleNode: config.titleNode,
      tabs: config.tabs,
      actions: config.actions,
    };
  });
}
